import { TRecaptchaStatus } from "../../reducers/RecaptchaStatusReducer";

/**
 * NG-7512: centralized Google reCaptcha availability detection.
 *
 * `react-google-recaptcha` lazily injects the reCaptcha `api.js` script on first widget mount
 * (public registration pages only) with no error/availability signal — so when the script fails
 * to load (Google outage, DNS/network failure, ad-block, geo-restriction) the widget silently
 * never appears and its `onErrored` callback never fires, leaving the submit button permanently
 * disabled. We instead inject `api.js` once at app startup and surface a definitive status
 * (loading | available | unavailable) via `onStatusChange`, so the forms can fail open.
 *
 * Because we inject first, the widget library reuses the already-present `window.grecaptcha`
 * rather than injecting a second time.
 *
 * Failure detection covers the outage classes:
 * - network / DNS / blocked (ad-block, geo) → `script.onerror`
 * - silent never-loads → detection timeout
 * - no site key configured → reported unavailable immediately (forms bypass)
 *
 * Note: unlike Google Maps there is no auth/billing failure global for reCaptcha — an invalid
 * site key only surfaces at render time on the widget itself, not during script load.
 */
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-sdk";
const RECAPTCHA_READY_CALLBACK = "__1hOnRecaptchaReady";
const DETECTION_TIMEOUT_MS = 10000;

let hasLoaded = false;

const isRecaptchaReady = (): boolean => typeof (window as any).grecaptcha?.render === "function";

export const loadRecaptcha = (siteKey: string, onStatusChange: (status: TRecaptchaStatus) => void): void => {
    // Already resolved by a prior call (HMR / remount) — short-circuit.
    if (isRecaptchaReady()) {
        onStatusChange("available");
        return;
    }
    if (hasLoaded) {
        return;
    }
    if (!siteKey) {
        onStatusChange("unavailable");
        return;
    }
    hasLoaded = true;

    onStatusChange("loading");

    let settled = false;
    let timeoutId = 0;

    const settle = (status: TRecaptchaStatus): void => {
        if (settled) {
            return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        onStatusChange(status);
    };

    timeoutId = window.setTimeout(() => settle("unavailable"), DETECTION_TIMEOUT_MS);

    // api.js invokes this global once grecaptcha is fully initialized (render=explicit defers
    // auto-rendering to the widget, so it will not touch our page).
    (window as any)[RECAPTCHA_READY_CALLBACK] = () => {
        settle(isRecaptchaReady() ? "available" : "unavailable");
    };

    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit&onload=${RECAPTCHA_READY_CALLBACK}`;
    script.onerror = () => settle("unavailable");
    document.head.appendChild(script);
};

/** Test-only: resets the module-level injection guard so each test starts fresh. */
export const resetRecaptchaLoaderForTests = (): void => {
    hasLoaded = false;
};
